// /src/data/demoReadService.js
// Client-side equivalents of the public database functions the demo group page calls.
//
// Every database function lives in `public` and reads `public` tables, so none of them can answer
// for demo ids, and a Content-Profile header cannot redirect them (the function would be looked up
// in `demo`, where none exists). While the demo read target is on, the data layer calls these
// instead. Each one mirrors its function's body exactly — same filters, same columns, same order.
//
// Also the demo route's gate and slug lookup (isAppointedDemoAdmin, getDemoGroupBySlug), and — the
// one write here, Phase B2 — the member_count twin of increment/decrement_member_count.
//
// ⚠ DUPLICATED LOGIC — keep in step. If a function below changes in the database, change its
// mirror here (and vice versa). Recorded in docs/reuse-backlog.md (18 Sep 2026) so the drift is a
// known thing to check. Architect ruling: docs/escalations/2026-09-18-phase-b1-schema-targeting-design.md
// Q3(a). The mirrors run only while the demo read target is on, so supabaseFetch sends them to `demo`.

import { supabaseFetch } from '../lib/supabase';

const MAX_HIERARCHY_DEPTH = 3; // Architecture.md § Nested Groups

/**
 * The /demo gate: is the signed-in caller an appointed demo admin? public.is_appointed_demo_admin()
 * (supabase/migrations/20260918b_demo_schema_create.sql) — the same function every demo table's RLS
 * policy uses. Any error (e.g. signed out: the function is revoked from anon) counts as "no".
 */
export async function isAppointedDemoAdmin() {
    const { data, error } = await supabaseFetch.rpc('is_appointed_demo_admin');
    return !error && data === true;
}

/**
 * The /demo/:slug lookup: an active demo group by slug, read from demo.communities explicitly
 * (schema 'demo') — never public's demo_slug column, so a public group carrying a colliding slug can
 * never be served, whatever the call order. No is_demo filter: every row in the demo schema is demo
 * by construction. Returns { data: row | null, error }.
 */
export async function getDemoGroupBySlug(demoSlug) {
    const { data, error } = await supabaseFetch.query('communities', {
        select: '*',
        eq: { demo_slug: demoSlug, demo_active: true },
        schema: 'demo'
    });
    return { data: data?.[0] || null, error };
}

/**
 * Mirrors public.get_child_groups(parent_uuid)
 * (supabase/migrations/20261016_group_being_closed.sql): direct children ordered by name, the same
 * 13 columns. The live function also lists a closing child to its own members; demo groups have no
 * members, so a closing demo child is never listed and is_closing is always false here.
 */
export async function demoGetChildGroups(parentId) {
    const result = await supabaseFetch.query('communities', {
        select: 'id,name,description,logo_url,member_count,article_count,hierarchy_level,visible_to_parent,is_private,auto_accept_members,parent_group_id,deletion_scheduled_at',
        eq: { parent_group_id: parentId },
        is: { deleted_at: null },
        order: 'name.asc'
    });
    if (Array.isArray(result?.data)) {
        result.data = result.data.map(row => ({ ...row, is_closing: false }));
    }
    return result;
}

/**
 * Mirrors public.get_group_hierarchy(group_uuid) (supabase/migrations/20260115_nested_groups.sql):
 * the group and its ancestors as { id, name, hierarchy_level, depth }, depth 0 = the group itself,
 * returned root first (ORDER BY depth DESC). One deliberate difference, not drift: the SQL recursion
 * has no depth bound, this walk stops at MAX_HIERARCHY_DEPTH ancestors — identical on valid data
 * (hierarchy is at most 3 levels), and a safety stop against a parent cycle.
 */
export async function demoGetGroupHierarchy(groupId) {
    const chain = [];
    let currentId = groupId;
    for (let depth = 0; currentId && depth <= MAX_HIERARCHY_DEPTH; depth++) {
        const { data, error } = await supabaseFetch.query('communities', {
            select: 'id,name,hierarchy_level,parent_group_id',
            eq: { id: currentId },
            single: true
        });
        if (error) return { data: null, error };
        if (!data) break;
        chain.push({ id: data.id, name: data.name, hierarchy_level: data.hierarchy_level, depth });
        currentId = data.parent_group_id;
    }
    return { data: chain.sort((a, b) => b.depth - a.depth), error: null };
}

/**
 * Mirrors public.get_group_events_by_start_time(p_community_id, p_limit NULL)
 * (supabase/migrations/20260813_events_ordering_by_start_time.sql): published events in the group,
 * LEFT JOIN event_details (an event with no details row is kept), ordered by
 * COALESCE(start_date, created_at) ascending, the same 9 columns. No deleted_at filter — the
 * function has none.
 */
export async function demoGetGroupEventsByStartTime(communityId) {
    const { data: events, error } = await supabaseFetch.query('articles', {
        select: 'id,title,content,summary,cover_image_url,author_id,community_id,view_count,created_at',
        eq: { article_type: 'event', is_published: true, community_id: communityId }
    });
    if (error) return { data: null, error };
    if (!events || events.length === 0) return { data: [], error: null };

    const { data: details, error: detailsError } = await supabaseFetch.query('event_details', {
        select: 'article_id,start_date',
        inFilter: { article_id: events.map(e => e.id) }
    });
    if (detailsError) return { data: null, error: detailsError };

    const startById = new Map((details || []).map(d => [d.article_id, d.start_date]));
    // articles.created_at is `timestamp` (no zone); the database compares it as UTC, but JS would
    // parse a zone-less string as local time — pin it to UTC so the order matches the function's.
    const asUtc = (ts) => (ts && !/([zZ]|[+-]\d\d:?\d\d)$/.test(ts) ? `${ts}Z` : ts);
    const sortKey = (e) => new Date(startById.get(e.id) || asUtc(e.created_at)).getTime();
    return { data: [...events].sort((a, b) => sortKey(a) - sortKey(b)), error: null };
}

/**
 * Twin of public.increment_member_count(community_id) / public.decrement_member_count(community_id)
 * (Phase B2: join/leave on a demo page). Neither function is in the repo's migrations; bodies as read
 * from production with pg_get_functiondef on 19 Sep 2026 (plpgsql, SECURITY INVOKER):
 *   increment: UPDATE communities SET member_count = member_count + 1 WHERE id = community_id;
 *   decrement: UPDATE communities SET member_count = GREATEST(member_count - 1, 0) WHERE id = community_id;
 * Same NULL behaviour: NULL + 1 stays NULL; GREATEST(NULL - 1, 0) is 0. One deliberate difference,
 * not drift: this is read-then-update, not one atomic UPDATE — two joins racing on the same demo
 * group could lose a count. Acceptable for an admin-only showcase (demo.communities.member_count
 * was already stale at the move, 20260918c). The update routes to `demo` because it carries the
 * demo group's id and member_count is the one communities column the wrapper lets through.
 * @param {string} communityId
 * @param {1|-1} delta
 */
export async function demoAdjustMemberCount(communityId, delta) {
    const { data, error } = await supabaseFetch.query('communities', {
        select: 'id,member_count',
        eq: { id: communityId },
        single: true
    });
    if (error) return { data: null, error };
    if (!data) return { data: null, error: null }; // no row: the UPDATE would match nothing either
    const current = data.member_count;
    const next = delta > 0
        ? (current === null ? null : current + 1)
        : (current === null ? 0 : Math.max(current - 1, 0));
    return supabaseFetch.update('communities', { member_count: next }, { eq: { id: communityId } });
}
